import { initialMetaForTable } from "constants/common";
import {
  getApprovalConfigurations,
  getExpenseCategories,
  getExpenseSubCategories,
} from "containers/configuration/api";
import { getMembers } from "containers/members/api";
import { getTemplates } from "containers/templates/api";
import { getUserByType } from "containers/users/api";
import { capitalizeName } from "./common";

const {
  getHealthPlans,
  getStates,
  getCounties,
  getCities,
  getZipCodes,
  getUnits,
  getProperties,
} = require("containers/policies/api");
const { debounce } = require("lodash");

const normalizeOption = (name) => {
  if (!name) return null;

  const clean = String(name).trim();

  return {
    value: clean
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, ""),
    label: clean,
  };
};
const useDebouncedMembersDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getMembers({ search: inputValue });

      const formattedOptions =
        response?.members?.map((member) => ({
          ...member,
          label: member.first_name,
          value: member.id,
        })) || [];
      const sorted = [...formattedOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      resolve(sorted);
      // resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedHousingNavigatorDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getUserByType({
        search: inputValue,
        user_type: "housing_navigator",
      });

      const formattedOptions =
        response?.housing_navigators?.map((member) => ({
          ...member,
          label: member.full_name,
          value: member.id,
        })) || [];
      const sorted = [...formattedOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      resolve(sorted);
      // resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedCaseManagerDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getUserByType({
        search: inputValue,
        user_type: "case_manager",
      });

      const formattedOptions =
        response?.cs_managers?.map((member) => ({
          ...member,
          label: member.full_name,
          value: member.id,
        })) || [];
      const sorted = [...formattedOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      resolve(sorted);
      // resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedHealthPlansDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getHealthPlans({ search: inputValue });

      const formattedOptions =
        response?.health_plans
          ?.filter((plan) => plan.status === "active")
          ?.map((manager) => ({
            label: manager.name,
            value: manager.id,
          })) || [];

      const sorted = [...formattedOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      resolve(sorted);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};

const useDebouncedStatesDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getStates({ search: inputValue });

      let formattedOptions =
        response?.states?.map((manager) => ({
          label: manager.name.toUpperCase(),
          value: manager.id,
        })) || [];
      const sorted = [...formattedOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      resolve(sorted);
      // resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedCountiesDataCallback = (delay = 700) => {
  return debounce(async (inputValue, stateId, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getCounties({
        search: inputValue,
        state_id: stateId,
      });

      let formattedOptions =
        response?.counties?.map((manager) => ({
          label: capitalizeName(manager.name),
          value: manager.id,
        })) || [];
      const sorted = [...formattedOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      resolve(sorted);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedCitiesDataCallback = (delay = 700) => {
  return debounce(async (inputValue, stateId, countyId, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getCities({
        search: inputValue,
        state_id: stateId,
        county_id: countyId,
      });

      let formattedOptions =
        response?.cities?.map((manager) => ({
          label: capitalizeName(manager.name),
          value: manager.id,
        })) || [];
      const sorted = [...formattedOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      resolve(sorted);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};

const useDebouncedTemplateDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getTemplates({
        ...initialMetaForTable,
        search: inputValue,
      });

      const formattedOptions =
        response?.templates?.map((manager) => ({
          label: manager.name,
          value: manager.id,
        })) || [];

      resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};

const useDebouncedCategoriesDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getExpenseCategories({
        ...initialMetaForTable,
        search: inputValue,
        status: "active",
      });

      const activeCategories =
        response?.expense_categories
          ?.filter((category) => category.status === "active")
          ?.map((category) => ({
            label: category.name,
            value: category.id,
          })) || [];
      const sorted = [...activeCategories].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      resolve(sorted);
      // resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedPaymentMethodsDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getApprovalConfigurations({
        ...initialMetaForTable,
        search: inputValue,
        status: "active",
      });

      const activePayments =
        response?.approval_configurations
          ?.filter((payment) => payment.status === "active")
          ?.map((payment) => ({
            label: payment.approval_method,
            value: payment.id,
          })) || [];

      const sorted = [...activePayments].sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      resolve(sorted);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedZipCodesDataCallback = (delay = 700) => {
  return debounce(async (inputValue, stateId, countyId, cityId, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getZipCodes({
        search: inputValue,
        state_id: stateId,
        county_id: countyId,
        city_id: cityId,
      });

      let formattedOptions =
        response?.zip_codes?.map((code) => ({
          label: code.name,
          value: code.id,
        })) || [];

      resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedAccountingDataCallback = (delay = 700) => {
  return debounce(async (inputValue, type, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getUserByType({
        ...initialMetaForTable,
        search: inputValue,
        user_type: type,
      });

      let formattedOptions =
        response?.accountings?.map((code) => ({
          label: code.full_name,
          value: code.id,
        })) || [];

      resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedExecutiveDataCallback = (delay = 700) => {
  return debounce(async (inputValue, type, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getUserByType({
        ...initialMetaForTable,
        search: inputValue,
        user_type: type,
      });

      let formattedOptions =
        response?.executives?.map((code) => ({
          label: code.full_name,
          value: code.id,
        })) || [];

      resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedUnitsDataCallback = (delay = 700) => {
  return debounce(async (inputValue, propertyId, resolve) => {
    if (!propertyId) {
      resolve([]);
      return;
    }

    try {
      const response = await getUnits({
        ...initialMetaForTable,
        propertyId: propertyId,
        search: inputValue?.trim() || "",
      });

      let formattedOptions =
        response?.housing_database?.units?.map((el) => ({
          value: el.id,
          label: capitalizeName(el.type_of_unit),
        })) || [];
      if (inputValue?.trim()) {
        formattedOptions = formattedOptions.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase()),
        );
      }

      resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching units:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedLandloadDataCallback = (delay = 700) => {
  return debounce(async (inputValue, propertyId, resolve) => {
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const response = await getUnits({
        ...initialMetaForTable,
        propertyId: propertyId,
        search: inputValue,
      });

      let formattedOptions =
        response?.housing_database?.key_contacts?.map((el) => ({
          value: el.id,
          label: el.name,
        })) || [];

      resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      resolve([]);
    }
  }, delay);
};

const useDebouncedSubCategoriesCallback = (delay = 500) => {
  return debounce(async (inputValue, categoryId, resolve) => {
    // If no categoryId, return empty
    if (!inputValue.trim()) {
      resolve([]);
      return;
    }

    try {
      const result = await getExpenseSubCategories({
        ...initialMetaForTable,
        expense_request_id: categoryId,
        search: inputValue,
        status: "active",
      });

      let formattedOptions =
        result?.expense_sub_categories?.map((el) => ({
          value: el.id,
          label: el.name,
        })) || [];

      resolve(formattedOptions);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      resolve([]);
    }
  }, delay);
};
const useDebouncedPropertiesDataCallback = (delay = 700) => {
  return debounce(async (inputValue, resolve) => {
    if (!inputValue?.trim()) {
      resolve([]);
      return;
    }

    try {
      const result = await getProperties({
        ...initialMetaForTable,
        search: inputValue,
      });

      const formattedOptions =
        result?.housing_databases?.map((el) => ({
          value: el.id,
          label: el.property_name || "",
        })) || [];

      const sorted = [...formattedOptions].sort((a, b) =>
        (a.label || "").localeCompare(b.label || ""),
      );

      resolve(sorted);
    } catch (error) {
      console.error("Error fetching properties:", error);
      resolve([]);
    }
  }, delay);
};
export {
  useDebouncedHealthPlansDataCallback,
  useDebouncedPropertiesDataCallback,
  useDebouncedTemplateDataCallback,
  useDebouncedStatesDataCallback,
  useDebouncedCountiesDataCallback,
  useDebouncedCitiesDataCallback,
  useDebouncedZipCodesDataCallback,
  useDebouncedMembersDataCallback,
  useDebouncedHousingNavigatorDataCallback,
  useDebouncedCaseManagerDataCallback,
  useDebouncedAccountingDataCallback,
  useDebouncedExecutiveDataCallback,
  useDebouncedCategoriesDataCallback,
  useDebouncedPaymentMethodsDataCallback,
  useDebouncedUnitsDataCallback,
  useDebouncedSubCategoriesCallback,
  useDebouncedLandloadDataCallback,
};
